import React from "react";
import "./AboutUs.css";
import buildingImg from "../../assets/falcom_holding_building.jpg";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../../context/LanguageContext";


const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" },
};

const AboutUs = () => {
  const { language } = useLanguage();
  const t = translations[language];

const companies = [
  {
    name: language === "ar" ? "يقين المالية" : "Yaqeen Capital",
    logo: "imgs/yaqeen.jpg"
  },
  {
    name: language === "ar" ? "شركة النايفات للتمويل" : "Nayifat Finance Company",
    logo: "imgs/nayifat_logo.svg"
  },
  {
    name: language === "ar" ? "شركة الأمثل للتمويل" : "Alamthal Financing",
    logo: "imgs/alamthal_logo.svg"
  }      
];


return (
  <AnimatePresence mode="wait">
    <motion.div
      key={language}
      className="aboutus-section"
      dir={language === "ar" ? "rtl" : "ltr"}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5, ease: "easeInOut" }}
    >

      {/* Background + Image + Triangle */}
      <motion.div
        className="blue-bg"
        initial={{ opacity: 0, x: 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      />
      <motion.img
        src={buildingImg}
        alt="Falcom building"
        className="building-image"
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      />
      <motion.div
        className="orange-triangle"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      />

      {/* Title */}
      <motion.h2
        className="aboutus-title"
        initial={{ opacity: 0, y: -20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        {t.title}
      </motion.h2>

      {/* Content */}
      <div className="aboutus-wrapper">
        <motion.div
          className="aboutus-left"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
        >
          {/* Texts */}
          <div className="aboutus-content">
            <motion.div className="aboutus-underline" {...fadeInUp} />

            <motion.div className="aboutus-block" {...fadeInUp}>
              <div className="icon-circle">
                <i className="fas fa-building"></i>
              </div>
              <div>
                <h3>{t.who}</h3>
                <p id="desc">{t.whoDesc}</p>
              </div>
            </motion.div>

            <motion.div className="aboutus-block" {...fadeInUp}>
              <div className="icon-circle">
                <i className="fas fa-bullseye"></i>
              </div>
              <div>
                <h3>{t.mission}</h3>
                <p id="desc">{t.missionDesc}</p>
              </div>
            </motion.div>

            {/* Pair of blocks */}
            <div className="aboutus-block-pair">
              <motion.div className="aboutus-block" {...fadeInUp}>
                <div className="icon-circle">
                  <i className="fas fa-lightbulb"></i>
                </div>
                <div>
                  <h3>{t.values}</h3>
                  <ul className="aboutus-list">
                    {t.valuesList.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              </motion.div>

              <motion.div className="aboutus-block" {...fadeInUp}>
                <div className="icon-circle">
                  <i className="fas fa-chart-line"></i>
                </div>
                <div>
                  <h3>{t.highlights}</h3>
                  <p className="aboutus-highlight">{t.highlightsDesc}</p>

                </div>
              </motion.div>
            </div>
          </div>
  
        </motion.div>

        {/* Right side image */}
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="aboutus-right"
        />

        <motion.div
        className="auto-slider"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        >
    <h3 className="our-companies-title">
    {language === "ar" ? "شركاتنا" : "Our Companies"}
  </h3>
              <motion.div className="our-companies-underline" {...fadeInUp} />

  <div className="slider-track"  style={{
    animation: language === "ar"
      ? "slide-rtl 20s linear infinite"
      : "slide-ltr 20s linear infinite"
  }}>
      {[...companies, ...companies, ...companies].map((company, index) => (
      <div key={index} className="slider-card">
        <img src={company.logo} alt={company.name} className="slider-logo" />
        <p className="slider-name">{company.name}</p>
      </div>
    ))}
  </div>
</motion.div>

      </div>
        </motion.div>
  </AnimatePresence>
);
};

const translations = {
  en: {
    title: "About Us",
    who: "Who We Are",
    whoDesc: "Falcom Holding is a Saudi closed joint-stock company, founded in 2017 in Riyadh. The company focuses on maximizing the shareholder's investments across strategic sectors in the Kingdom.",
    mission: "Our Mission",
    missionDesc: "To lead sustainable and diversified investments that contribute to Saudi Arabia's economic growth.",
    values: "Core Values",
    valuesList: [
      "Innovation",
      "Transparency",
      "Long-term Vision",
      "Professionalism"
    ],
    highlights: "Highlights",
    highlightsDesc: "Falcom Holding has achieved remarkable growth in diversified sectors since its establishment, contributing to national economic transformation.",
    foundedYear: "Falcom Holding was founded",
    expansionYear: "Strategic expansion of investments",
    
  },
  ar: {
    title: "من نحن",
    who: "من نحن",
    whoDesc: "فالكم القابضة هي شركة مساهمة مقفلة سعودية، تأسست عام 2017 في الرياض. تركز الشركة على تعظيم استثمارات المساهمين في قطاعات استراتيجية داخل المملكة.",
    mission: "رسالتنا",
    missionDesc: "الريادة في الاستثمارات المستدامة والمتنوعة التي تساهم في نمو الاقتصاد السعودي.",
    values: "القيم الأساسية",
    valuesList: [
      "الابتكار",
      "الشفافية",
      "رؤية طويلة المدى",
      "الاحترافية"
    ],
    highlights: "أبرز الإنجازات",
    highlightsDesc: "حققت فالكم القابضة نموًا ملحوظًا في قطاعات متنوعة منذ تأسيسها، مما ساهم في التحول الاقتصادي الوطني.",
    foundedYear: "تأسست شركة فالكم القابضة",
    expansionYear: "توسع استراتيجي في الاستثمارات",

  }
};


export default AboutUs;
